import { Option } from '@rivethealth/data/options';
import { isEqual } from 'lodash';
import { BehaviorSubject, defer, Observable, Observer, Subject } from 'rxjs';
import { v4 } from 'uuid';
import { Loadable, loadMap } from '../helpers/loadable';
import { hookStateManager } from './hook';

//#region React basic hooks

export interface DependencyList extends ReadonlyArray<any> {}

function depsEqual(
  a: DependencyList | undefined,
  b: DependencyList | undefined,
): boolean {
  return (
    a !== undefined &&
    b !== undefined &&
    a.length === b.length &&
    a.every((value, i) => isEqual(value, b[i]))
  );
}

/**
 * @see https://reactjs.org/docs/hooks-reference.html#usestate
 * WARNING:
 *   We store states as an array.
 *   We retrieve states based on how many times we call useState in the current iteration of the _ function.
 *   Note that this will lead to weird behavior if you call useState in a conditional.
 */
export function useState<T>(initial: T | (() => T)): [T, (value: T) => void] {
  const stateManager = hookStateManager();
  if (typeof initial !== 'function') {
    initial = (function (initial) {
      return () => initial;
    })(initial);
  }
  const state = stateManager.nextState(<() => T>initial);
  return [
    state.get(),
    value => {
      state.set(value);
      stateManager.invalidate();
    },
  ];
}

export function useOptions<T>(
  initial: T | (() => T),
  options: Option[],
): { value: T; setValue: (value: T) => void; options: Option[] } {
  const [value, setValue] = useState(initial);
  return {
    value,
    setValue,
    options,
  };
}

export function useDebouncedState<T>(
  initial: T | (() => T),
  timeout: number,
): [T, (value: T) => void] {
  const stateManager = hookStateManager();
  const [timeoutId, setTimeoutId] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  if (typeof initial !== 'function') {
    initial = (function (initial) {
      return () => initial;
    })(initial);
  }
  const state = stateManager.nextState(<() => T>initial);
  return [
    state.get(),
    value => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
      setTimeoutId(
        setTimeout(() => {
          state.set(value);
          stateManager.invalidate();
        }, timeout),
      );
    },
  ];
}

interface EffectState {
  cleanup: Destructor | void;
  deps: DependencyList | undefined;
}

export interface EffectCallback {
  (): Destructor | void;
}

export interface Destructor {
  (): void;
}

/**
 * @see https://reactjs.org/docs/hooks-reference.html#useeffect
 *
 * Unlike React, checks deps for deep equality rather than shallow.
 */
export function useEffect(
  fn: EffectCallback,
  deps?: DependencyList,
  immediate: boolean = false,
): void {
  const stateManager = hookStateManager();
  const state = stateManager.nextState<EffectState | undefined>(
    () => undefined,
    state => state!.cleanup && state!.cleanup(),
  );
  const value = state.get();
  if (depsEqual(value?.deps, deps)) {
    return;
  }
  if (value?.cleanup) {
    value.cleanup();
  }
  if (immediate) {
    state.set({ cleanup: fn(), deps });
  } else {
    Promise.resolve().then(() => state.set({ cleanup: fn(), deps }));
  }
}

export function useDebouncedEffect(
  fn: EffectCallback,
  timeout: number,
  deps?: DependencyList,
): void {
  const [timeoutId, setTimeoutId] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  useEffect(() => {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }
    setTimeoutId(
      setTimeout(() => {
        fn();
      }, timeout),
    );
  }, deps);
}

// useContext not implemented.
// Use Angular DI instead.

//#endregion

//#region React additional hooks

/**
 * TODO
 * @see https://reactjs.org/docs/hooks-reference.html#usereducer
 */
export const useReducer: any = () => {
  throw new Error('Unsupported');
};

/**
 * @see https://reactjs.org/docs/hooks-reference.html#usecallback
 */
export function useCallback<T>(
  factory: () => T,
  deps: DependencyList,
): () => T {
  return useMemo(() => factory, deps);
}

interface MemoState<T> {
  deps: DependencyList | undefined;
  value: T;
}

/**
 * @see https://reactjs.org/docs/hooks-reference.html#usememo
 *
 * Unlike React, checks deps for deep equality rather than shallow.
 */
export function useMemo<T>(
  factory: () => T,
  deps: DependencyList | undefined,
): T {
  const stateManager = hookStateManager();
  const state = stateManager.nextState<MemoState<T> | undefined>(
    () => undefined,
  );
  const value = state.get();
  if (depsEqual(value?.deps, deps)) {
    return value!.value;
  }
  const result = factory();
  state.set({ deps, value: result });
  return result;
}

export class MutableRef<T> {
  constructor(public current: T) {}
}

/**
 * https://reactjs.org/docs/hooks-reference.html#useref
 */
export function useRef<T>(): MutableRef<T | undefined>;
export function useRef<T>(initial: T): MutableRef<T>;
export function useRef<T>(initial?: T) {
  const stateManager = hookStateManager();
  const state = stateManager.nextState(() => new MutableRef(initial));
  return state.get();
}

// useImperativeHandle not implemented

// useLayoutEffect not implemented.
// useEffect already runs synchronously

// useDebugValue not implemented.
// React dev tools are not applicable.

// useDeferredValue not implemented.
// updates run synchronously

// useTransition not implemented.
// React transitions are not applicable.

// useId not implemented.
// Hydration is not not applicable to Angular.

//#endregion

//#region Rivet hooks

/**
 * Equivalent to useMemo(fn, [])
 */
export function useInit<T>(fn: () => T): T {
  const stateManager = hookStateManager();
  const state = stateManager.nextState(fn);
  return state.get();
}

/**
 * Observable of values.
 */
export function useObservable<T>(input: T): Observable<T> {
  const stateManager = hookStateManager();
  const state = stateManager.nextState(() => new BehaviorSubject(input));
  const value = state.get();
  if (value.value !== input) {
    value.next(input);
  }
  return value;
}

/**
 * Subscribe
 */
export function useSubscription<T>(
  observable: Observable<T>,
  observer: Partial<Observer<T>> | undefined,
  deps: DependencyList,
): void {
  return useEffect(
    () => {
      const subscription = observable.subscribe(observer);
      return () => {
        subscription.unsubscribe();
      };
    },
    deps,
    true,
  );
}

interface SubscriptionResult<T> {
  (): T | undefined;
}

/**
 * Subscribe and return the lastest value
 */
export function useSubscriptionLatest<T>(
  observable: Observable<T>,
  deps: DependencyList,
): T | undefined {
  // Typically, state should be treated as immutable, and only the set function
  // should be used to trigger updates to it. As a performance optimization, we
  // mutate it directly here so that observables who synchronously respond to
  // subscribe() calls can have a value available on first render rather than
  // second render. The return type remains T | undefined for observables that
  // are truly asynchronous.
  let [result, setResult] = useState<SubscriptionResult<T>>(
    () => () => undefined,
  );

  useSubscription(
    observable,
    {
      error: error => {
        result = () => {
          throw error;
        };
        setResult(result);
      },
      next: value => {
        result = () => value;
        setResult(result);
      },
    },
    deps,
  );

  return result();
}

/**
 * Use cached value, if equal to current value
 */
export function useCached<T>(value: T, equal: (a: T, b: T) => boolean) {
  const stateManager = hookStateManager();
  const state = stateManager.nextState(() => value);
  const stored = state.get();
  if (equal(stored, value)) {
    return stored;
  }
  state.set(value);
  return value;
}

/**
 * Unwrap a promised value. Returns undefined if the promise hasn't resolved
 * yet. Will trigger another hooks run when the promise resolves.
 */
export function usePromise<T>(promisedValue: Promise<T>): T | undefined {
  const [value, setValue] = useState<T | undefined>(undefined);
  if (!value) {
    promisedValue.then(setValue);
  }
  return value;
}

/**
 * Wrap values of an observable (or a factory that produces one) in a Loadable,
 * resubscribing when deps change.
 *
 * Unlike React, checks deps for deep equality rather than shallow.
 */
export function useLoad<T>(
  observableOrFactory: Observable<T> | (() => Observable<T>),
  deps: DependencyList,
): [Loadable<T>, () => void] {
  const ref = useRef<Observable<T>>();
  ref.current =
    typeof observableOrFactory === 'function'
      ? defer(observableOrFactory)
      : observableOrFactory;

  const [trigger, nextTrigger] = useTrigger();
  const input = useObservable(useCached([...deps, trigger], depsEqual));

  const loadable = useSubscriptionLatest(
    input.pipe(loadMap(() => ref.current!)),
    [],
  );

  return [loadable || { loading: true, data: null, erred: false }, nextTrigger];
}

/**
 * Load observable when a dependency changes, debouncing for some number of milliseconds.
 * Returns a loadable and a trigger that skips the debounce.
 */
export function useLoadWithDebounce<T>(
  observable: Observable<T> | (() => Observable<T>),
  deps: DependencyList,
  timeout: number,
): [Loadable<T>, () => void] {
  const [loadable, skipDebounceTrigger] = useTriggerLoad(observable);
  useDebouncedEffect(
    () => {
      skipDebounceTrigger();
    },
    timeout,
    deps,
  );

  return [loadable, skipDebounceTrigger];
}

/**
 * Load observable when a dependency changes, debouncing for some number of milliseconds.
 * Returns a loadable and a trigger that skips the debounce.
 * Does not debounce on the first load.
 */
export function useLoadImmediateThenDebounce<T>(
  observable: Observable<T> | (() => Observable<T>),
  deps: DependencyList,
  timeout: number,
): [Loadable<T>, () => void] {
  const [loadable, skipDebounceTrigger] = useTriggerLoad(observable);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Use an effect to handle the initial load
  useEffect(() => {
    if (isInitialLoad) {
      skipDebounceTrigger(); // Trigger the load immediately on initial render
      setIsInitialLoad(false); // Set initial load to false so subsequent renders do not trigger this
    }
  }, [isInitialLoad]);

  // This effect handles debouncing for all updates after the initial load
  useDebouncedEffect(
    () => {
      if (!isInitialLoad) {
        // Only debounce after the initial load has been handled
        skipDebounceTrigger();
      }
    },
    timeout,
    deps,
  );

  return [loadable, skipDebounceTrigger];
}

/**
 * Load observable when a dependency changes, debouncing for some number of milliseconds.
 * Returns a loadable, a trigger that skips the debounce, and whether the debounce is currently happening.
 */
export function useLoadWithWatchedDebounce<T>(
  observable: Observable<T> | (() => Observable<T>),
  deps: DependencyList,
  timeout: number,
): [Loadable<T>, () => void, boolean] {
  const [loadable, skipDebounceTrigger] = useTriggerLoad(observable);
  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);
  const [timeoutId, setTimeoutId] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  useEffect(() => {
    setIsDebouncing(true);
    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }
    setTimeoutId(
      setTimeout(() => {
        skipDebounceTrigger();
        setIsDebouncing(false);
      }, timeout),
    );
  }, deps);
  return [loadable, skipDebounceTrigger, isDebouncing];
}

/**
 * Load observable when trigger is called.
 */
export function useTriggerLoad<T>(
  observableOrFactory: Observable<T> | (() => Observable<T>),
): [Loadable<T>, () => void] {
  const ref = useRef<Observable<T>>();
  ref.current =
    typeof observableOrFactory === 'function'
      ? defer(observableOrFactory)
      : observableOrFactory;
  const subject = useInit(() => new Subject<undefined>());
  const loadable = useSubscriptionLatest(
    subject.pipe(loadMap(() => ref.current!)),
    [],
  );
  return [
    loadable || { loading: true, data: null, erred: false },
    () => subject.next(undefined),
  ];
}

/**
 * Load observable when trigger is called. Trigger accepts single argument'
 */
export function useTriggerLoadWithArg<T, A>(
  observableFactory: (arg: A) => Observable<T>,
): [Loadable<T>, (arg: A) => void] {
  const ref = useRef<(arg: A) => Observable<T>>();
  ref.current = observableFactory;

  const subject = useInit(() => new Subject<A>());
  const loadable = useSubscriptionLatest(
    subject.pipe(loadMap((arg: A) => ref.current!(arg))),
    [],
  );

  return [
    loadable || { loading: true, data: null, erred: false },
    (arg: A) => subject.next(arg),
  ];
}

/**
 * Load observable when trigger is called, debounced
 */
export function useTriggerLoadDebounce<T>(
  observableOrFactory: Observable<T> | (() => Observable<T>),
  timeout: number,
): [Loadable<T>, () => void] {
  const [loadable, _trigger] = useTriggerLoad(observableOrFactory);
  const [timeoutId, setTimeoutId] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const trigger = () => {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }
    setTimeoutId(setTimeout(_trigger, timeout));
  };
  return [loadable, trigger];
}

/**
 * Load observable immediately upon a condition being true, and when trigger is called.
 */
export function useTriggerLoadAndInit<T>(
  observable: Observable<T> | (() => Observable<T>),
  condition?: boolean,
): [Loadable<T>, () => void] {
  const [firstRun, setFirstRun] = useState(true);
  const [loadable, trigger] = useTriggerLoad(observable);
  useEffect(() => {
    if (!loadable.data && !loadable.loading && firstRun && condition) {
      trigger();
      setFirstRun(false);
    }
  }, [firstRun, condition, loadable]);
  return [loadable, trigger];
}

export type Trigger = number;

/**
 * Use a triggerable value that changes
 */
export function useTrigger(): [Trigger, () => void] {
  const [value, setValue] = useState(0);
  return [value, () => setValue(value + 1)];
}
/**
 * Use a unique ID that shouldn't change.
 */
export function useUUID(): string {
  const [memoizedUUID] = useMemo(() => {
    return v4();
  }, []);
  return memoizedUUID;
}

/**
 * Similar to RxJS pairwise(), but due to the nature of hooks, this hook will
 * always return a value. To make it act like pairwise(), wait until `previous`
 * has a value, e.g.
 * `` const [previous, current] = usePairwise(value); if (!previous) return; ``
 */
export function usePairwise<T>(value: T): [T | undefined, T] {
  const [previous, setPrevious] = useState<T | undefined>(undefined);
  const [current, setCurrent] = useState<T>(value);
  useEffect(() => {
    setPrevious(current);
    setCurrent(value);
  }, [value]);
  return [previous, current];
}

/**
 * Handles timeouts using the [`setTimeout API`](https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/setTimeout).
 * Adapted from [usehooks-ts](https://github.com/juliencrn/usehooks-ts/blob/master/packages/usehooks-ts/src/useTimeout/useTimeout.ts).
 * @param {() => void} callback - The function to be executed when the timeout elapses.
 * @param {number | null} delay - The duration (in milliseconds) for the timeout. Set to `null` to clear the timeout.
 */
export function useTimeout(callback: () => void, delay: number | null): void {
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  useEffect(() => {
    if (delay == null) {
      return;
    }
    const id = setTimeout(() => {
      savedCallback.current();
    }, delay);
    return () => {
      clearTimeout(id);
    };
  }, [delay]);
}

/**
 * Creates an interval that invokes a callback function at a specified delay using the [`setInterval API`](https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/setInterval).
 * Adapted from [usehooks-ts](https://github.com/juliencrn/usehooks-ts/blob/master/packages/usehooks-ts/src/useInterval/useInterval.ts).
 * @param {() => void} callback - The function to be invoked at each interval.
 * @param {number | null} delay - The time, in milliseconds, between each invocation of the callback. Use `null` to clear the interval.
 */
export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  useEffect(() => {
    if (delay == null) {
      return;
    }
    const id = setInterval(() => {
      savedCallback.current();
    }, delay);
    return () => {
      clearInterval(id);
    };
  }, [delay]);
}

/**
 * Use state that uses local storage to load value
 */
export function useLocalStorageState(
  stateKey: string,
  defaultValue?: string,
): [string | null, (value: string) => void] {
  const firstVal = localStorage.getItem(stateKey) || defaultValue || null;
  const [value, setValue] = useState<string | null>(firstVal);
  return [
    value,
    newVal => {
      setValue(newVal);
      localStorage.setItem(stateKey, newVal);
    },
  ];
}

//#endregion
